import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PlanSimpleDTO, Feature } from '../../../types/plans/Plans';

export interface ISystemPlanState {
  plans: PlanSimpleDTO[];

  editingFeatures: Feature[];
  originalFeatures: Feature[];
  editingPlanCode: string | null;
  isLoadingFeatures: boolean;
}

export const initialState: ISystemPlanState = {
  plans: [],

  editingFeatures: [],
  originalFeatures: [],
  editingPlanCode: null,
  isLoadingFeatures: false,
};

export const systemPlanSlice = createSlice({
  name: 'systemPlans',
  initialState,
  reducers: {
    /**
     * 📦 Cargar planes (ligeros)
     */
    loadingSystemPlans: (state, action: PayloadAction<PlanSimpleDTO[]>) => {
      state.plans = action.payload;
    },

    /**
     * 🔄 Cambiar plan activo (solo local)
     */
    updateActivePlan: (state, action: PayloadAction<string>) => {
      const planCode = action.payload;

      state.plans = state.plans.map((plan) => ({
        ...plan,
        active: plan.code === planCode,
      }));
    },

    /**
     * 🔥 Cargar features para edición
     */
    setEditingFeatures: (
      state,
      action: PayloadAction<{ planCode: string; features: Feature[] }>
    ) => {
      state.editingPlanCode = action.payload.planCode;
      state.editingFeatures = action.payload.features;

      // 🧠 copia segura (importante)
      state.originalFeatures = action.payload.features.map((f) => ({ ...f }));
    },

    /**
     * 🔁 Toggle feature
     */
    toggleFeature: (state, action: PayloadAction<string>) => {
      state.editingFeatures = state.editingFeatures.map((f) =>
        f.code === action.payload ? { ...f, enabled: !f.enabled } : f
      );
    },

    setFeatureEnabled: (
      state,
      action: PayloadAction<{ code: string; enabled: boolean }>
    ) => {
      state.editingFeatures = state.editingFeatures.map((f) =>
        f.code === action.payload.code
          ? { ...f, enabled: action.payload.enabled }
          : f
      );
    },

    /**
     * ❌ Cancelar edición (rollback)
     */
    resetEditingFeatures: (state) => {
      state.editingFeatures = state.originalFeatures.map((f) => ({ ...f }));
    },

    /**
     * 🧹 Limpiar edición (cuando cambias de plan)
     */
    clearEditingFeatures: (state) => {
      state.editingFeatures = [];
      state.originalFeatures = [];
      state.editingPlanCode = null;
    },

    startLoadingFeatures: (state) => {
      state.isLoadingFeatures = true;
    },

    stopLoadingFeatures: (state) => {
      state.isLoadingFeatures = false;
    },
  },
});

export const {
  loadingSystemPlans,
  updateActivePlan,
  setEditingFeatures,
  toggleFeature,
  setFeatureEnabled,
  resetEditingFeatures,
  clearEditingFeatures,
  startLoadingFeatures,
  stopLoadingFeatures,
} = systemPlanSlice.actions;

export default systemPlanSlice.reducer;
